import { useState } from 'react'
import {
  ChevronDown, Database, UserPlus, Video, Youtube, Image,
  Briefcase, Play, GitBranch, BarChart3,
  LineChart, PieChart, ArrowLeftRight, Filter, AlertCircle, Info
} from 'lucide-react'
import AnchorLink from './AnchorLink'

const SubSectionHeading = ({ children, id }) => (
  <div className="mb-5" id={id}>
    <div className="group flex items-center gap-2">
      <h3 className="wiki-heading text-lg font-semibold text-light-text-primary dark:text-dark-text-primary">
        {children}
      </h3>
      {id && <AnchorLink id={id} />}
    </div>
    <div className="mt-2 h-px bg-light-border-primary dark:bg-dark-border-primary" />
  </div>
)

const Tag_ = ({ children, color = 'gray' }) => {
  const colors = {
    blue:   'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800',
    green:  'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 border-green-200 dark:border-green-800',
    amber:  'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-800',
    purple: 'bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300 border-purple-200 dark:border-purple-800',
    gray:   'bg-light-bg-tertiary dark:bg-dark-bg-tertiary text-light-text-secondary dark:text-dark-text-secondary border-light-border-primary dark:border-dark-border-primary',
  }
  return (
    <span className={`wiki-mono inline-flex items-center text-[11px] px-1.5 py-0.5 rounded border ${colors[color]}`}>
      {children}
    </span>
  )
}

const MetricRow = ({ label, value }) => (
  <div className="grid grid-cols-[120px_1fr] gap-3 text-xs py-2 border-b border-light-border-primary dark:border-dark-border-primary last:border-0">
    <span className="wiki-mono text-[11px] uppercase tracking-wider text-light-text-tertiary dark:text-dark-text-tertiary pt-0.5">
      {label}
    </span>
    <span className="text-light-text-secondary dark:text-dark-text-secondary leading-relaxed">
      {value}
    </span>
  </div>
)

const metrics = [
  {
    id: 'stats-talents-added',
    icon: UserPlus,
    color: 'text-blue-600 dark:text-blue-400',
    bg: 'bg-blue-50 dark:bg-blue-900/20',
    dot: 'bg-blue-500',
    title: 'Talents Added',
    route: '/statistics/managers-talents',
    what: 'Number of talent records created by each manager, broken down by type (candidate / employee / presale). Also separately counts type conversions.',
    events: [
      <><Tag_ color="gray">talent_created</Tag_><span className="text-light-text-tertiary dark:text-dark-text-tertiary"> — main event, one per new talent</span></>,
      <><Tag_ color="gray">talent_type_changed</Tag_><span className="text-light-text-tertiary dark:text-dark-text-tertiary"> — counted as conversions (candidate → employee / presale)</span></>,
    ],
    calc: 'COUNT of events grouped by user_id. Talent type is extracted from event_data.initial_data. Conversions are counted in a separate column.',
    filters: 'Date range · Talent type (candidate / employee / presale) · Manager(s) · Period grouping (day / week / month)',
    note: 'Credit goes to the user who created the record — even if they later leave the team.',
  },
  {
    id: 'stats-videos',
    icon: Video,
    color: 'text-indigo-600 dark:text-indigo-400',
    bg: 'bg-indigo-50 dark:bg-indigo-900/20',
    dot: 'bg-indigo-500',
    title: 'Videos Added',
    route: '/statistics/managers-videos',
    what: 'Number of video files uploaded to talent profiles by each manager.',
    events: [
      <><Tag_ color="gray">media_uploaded</Tag_><span className="text-light-text-tertiary dark:text-dark-text-tertiary"> — where media_type = "video"</span></>,
      <><Tag_ color="gray">media_record_created</Tag_><span className="text-light-text-tertiary dark:text-dark-text-tertiary"> — where media_type = "video"</span></>,
    ],
    calc: 'COUNT DISTINCT of media_id. Deduplication prevents a single file from being counted twice if multiple events fire for the same upload.',
    filters: 'Date range · Manager(s) · Period grouping',
    note: 'Only media that still exists in the database is counted — deleted files are excluded from the total.',
  },
  {
    id: 'stats-youtube',
    icon: Youtube,
    color: 'text-red-600 dark:text-red-400',
    bg: 'bg-red-50 dark:bg-red-900/20',
    dot: 'bg-red-500',
    title: 'YouTube Added',
    route: '/statistics/managers-youtube',
    what: 'Number of YouTube and YouTube Shorts links added to talent profiles.',
    events: [
      <><Tag_ color="gray">media_uploaded</Tag_><span className="text-light-text-tertiary dark:text-dark-text-tertiary"> — where media_type IN ("youtube", "youtube_shorts")</span></>,
      <><Tag_ color="gray">media_record_created</Tag_><span className="text-light-text-tertiary dark:text-dark-text-tertiary"> — where media_type IN ("youtube", "youtube_shorts")</span></>,
    ],
    calc: 'COUNT DISTINCT of media_id — same deduplication logic as Videos Added.',
    filters: 'Date range · Manager(s) · Period grouping',
    note: 'YouTube and YouTube Shorts are tracked together in one metric.',
  },
  {
    id: 'stats-portraits',
    icon: Image,
    color: 'text-pink-600 dark:text-pink-400',
    bg: 'bg-pink-50 dark:bg-pink-900/20',
    dot: 'bg-pink-500',
    title: 'Portraits Added',
    route: '/statistics/managers-portrait',
    what: 'Number of profile photos (portrait images) uploaded to talent profiles.',
    events: [
      <><Tag_ color="gray">media_uploaded</Tag_><span className="text-light-text-tertiary dark:text-dark-text-tertiary"> — where media_type = "portrait"</span></>,
      <><Tag_ color="gray">media_record_created</Tag_><span className="text-light-text-tertiary dark:text-dark-text-tertiary"> — where media_type = "portrait"</span></>,
    ],
    calc: 'COUNT DISTINCT of media_id.',
    filters: 'Date range · Manager(s) · Period grouping',
    note: null,
  },
  {
    id: 'stats-hired',
    icon: Briefcase,
    color: 'text-green-600 dark:text-green-400',
    bg: 'bg-green-50 dark:bg-green-900/20',
    dot: 'bg-green-500',
    title: 'Hired',
    route: '/statistics/managers-hired',
    what: 'Number of talents whose status was changed to "Hired" within the selected period.',
    events: [
      <><Tag_ color="gray">status_changed</Tag_><span className="text-light-text-tertiary dark:text-dark-text-tertiary"> — where to_status_name = "hired" (case-insensitive)</span></>,
    ],
    calc: 'COUNT of matching events. Manager is resolved via COALESCE(event_data.manager_id, user_id) — the talent\'s assigned manager takes priority over whoever clicked the button.',
    filters: 'Date range · Manager(s) · Period grouping',
    note: 'This counts status change events, not current employee count. Someone hired in January will appear in January\'s stat even if they later left.',
  },
  {
    id: 'stats-started',
    icon: Play,
    color: 'text-teal-600 dark:text-teal-400',
    bg: 'bg-teal-50 dark:bg-teal-900/20',
    dot: 'bg-teal-500',
    title: 'Started',
    route: '/statistics/managers-started',
    what: 'Number of talents whose status was changed to "Start" (began working) within the selected period.',
    events: [
      <><Tag_ color="gray">status_changed</Tag_><span className="text-light-text-tertiary dark:text-dark-text-tertiary"> — where to_status_name = "start" (case-insensitive)</span></>,
    ],
    calc: 'Same logic as Hired — COUNT with COALESCE manager resolution.',
    filters: 'Date range · Manager(s) · Period grouping',
    note: 'Tracks when work actually began, not when the hiring decision was made.',
  },
  {
    id: 'stats-onboarding',
    icon: GitBranch,
    color: 'text-amber-600 dark:text-amber-400',
    bg: 'bg-amber-50 dark:bg-amber-900/20',
    dot: 'bg-amber-500',
    title: 'Onboarding Funnel',
    route: '/statistics/onboarding',
    what: 'Tracks how many candidates entered the Onboarding stage and what happened next — passed or failed.',
    events: [
      <><Tag_ color="gray">status_changed → Onboarding</Tag_><span className="text-light-text-tertiary dark:text-dark-text-tertiary"> — entry event</span></>,
      <><Tag_ color="gray">status_changed from Onboarding</Tag_><span className="text-light-text-tertiary dark:text-dark-text-tertiary"> — exit event, outcome depends on target status</span></>,
    ],
    calc: (
      <div className="space-y-1.5">
        <div className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-amber-400 flex-shrink-0" /><span><strong className="text-light-text-primary dark:text-dark-text-primary">Entered</strong> — moved TO status "Onboarding"</span></div>
        <div className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-green-400 flex-shrink-0" /><span><strong className="text-light-text-primary dark:text-dark-text-primary">Passed</strong> — exited TO a success status (e.g. Hired)</span></div>
        <div className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-red-400 flex-shrink-0" /><span><strong className="text-light-text-primary dark:text-dark-text-primary">Failed</strong> — exited TO a rejection status (Refused, Denied, Not Approved)</span></div>
      </div>
    ),
    filters: 'Date range · Manager(s)',
    note: 'Success and failure statuses are configured in shared/talentStatuses.json.',
  },
  {
    id: 'stats-custom',
    icon: BarChart3,
    color: 'text-purple-600 dark:text-purple-400',
    bg: 'bg-purple-50 dark:bg-purple-900/20',
    dot: 'bg-purple-500',
    title: 'Custom Reports',
    route: '/statistics/custom-reports',
    what: 'Flexible report builder — count talents grouped by any combination of up to 3 dimensions.',
    events: null,
    calc: 'Direct query on the talents table (NOT the events table). Groups by selected dimensions and returns COUNT per combination.',
    filters: 'Dimensions (up to 3): talent type · current status · manager · country · city · profession · department · creation month/year',
    note: 'Custom Reports reflect the CURRENT state of talent profiles. Deleting a talent removes it immediately. Other stats (event-based) are unaffected.',
  },
]

const faqItems = [
  {
    key: 'stats-q1',
    question: "Why doesn't the Hired count match the number of employees?",
    answer: (
      <div className="text-sm text-light-text-secondary dark:text-dark-text-secondary space-y-2">
        <p>They measure different things:</p>
        <ul className="space-y-1 ml-4">
          <li>• <strong className="text-light-text-primary dark:text-dark-text-primary">Hired stat</strong> — counts status change events to "hired" within the selected date range.</li>
          <li>• <strong className="text-light-text-primary dark:text-dark-text-primary">Employees section</strong> — shows people whose current type is employee and who are currently active.</li>
        </ul>
        <p>Someone can be hired then leave, or be converted to Presale — they'll still appear in the Hired stat for the period they were hired.</p>
      </div>
    )
  },
  {
    key: 'stats-q2',
    question: 'Why is a talent counted under a different manager than expected?',
    answer: (
      <div className="text-sm text-light-text-secondary dark:text-dark-text-secondary space-y-2">
        <p>For <strong>Hired</strong> and <strong>Started</strong>, manager is resolved via <Tag_ color="gray">COALESCE(event_data.manager_id, user_id)</Tag_>.</p>
        <p>This uses the <em>talent's assigned manager</em> at the time of the status change — not who clicked the button. If no manager was set on the talent, it falls back to the user who performed the action.</p>
      </div>
    )
  },
  {
    key: 'stats-q3',
    question: 'Does editing a talent profile affect statistics?',
    answer: (
      <div className="text-sm text-light-text-secondary dark:text-dark-text-secondary space-y-2">
        <p><strong className="text-light-text-primary dark:text-dark-text-primary">No</strong> — for all event-based metrics (Talents Added, Videos, YouTube, Portraits, Hired, Started, Onboarding). These read from the immutable <Tag_ color="gray">events</Tag_> table.</p>
        <p><strong className="text-light-text-primary dark:text-dark-text-primary">Yes</strong> — for <strong>Custom Reports</strong>, which query the <Tag_ color="gray">talents</Tag_> table directly.</p>
      </div>
    )
  },
  {
    key: 'stats-q4',
    question: 'Custom Reports shows different numbers — why?',
    answer: (
      <div className="text-sm text-light-text-secondary dark:text-dark-text-secondary space-y-2">
        <p>Custom Reports query the <strong>current state</strong> of the talents table — a snapshot of right now.</p>
        <p>Other statistics query the <strong>events history</strong> — what happened over time, regardless of current state.</p>
        <p>Example: a candidate created in January and converted to employee in March will appear in <em>Talents Added</em> for January, but Custom Reports (grouped by type) will show them as <em>employee</em>.</p>
      </div>
    )
  },
]

const StatisticsGuide = () => {
  const [expandedBlocks, setExpandedBlocks] = useState({})
  const toggle = (key) => setExpandedBlocks(prev => ({ ...prev, [key]: !prev[key] }))

  return (
    <div className="space-y-10">

      {/* Intro + data source */}
      <div id="stats-overview">
        <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary leading-relaxed mb-4">
          The Statistics section tracks manager activity across 8 metric types.
          Most metrics are calculated from an <strong className="text-light-text-primary dark:text-dark-text-primary">immutable events table</strong> —
          numbers reflect what happened at the time of the action, not the current state of talent profiles.
        </p>
        <div className="pl-4 border-l-2 border-primary-500/40 bg-primary-50/50 dark:bg-primary-900/10 rounded-r-lg py-3 pr-4 space-y-2">
          <p className="text-sm font-semibold text-light-text-primary dark:text-dark-text-primary flex items-center gap-2">
            <Database className="w-4 h-4 text-primary-500" /> Data sources
          </p>
          <div className="space-y-1.5 text-sm text-light-text-secondary dark:text-dark-text-secondary">
            <div className="flex items-start gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-primary-500 flex-shrink-0 mt-1.5" />
              <span><strong className="text-light-text-primary dark:text-dark-text-primary">events table</strong> — used by all metrics except Custom Reports.</span>
            </div>
            <div className="flex items-start gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-light-text-tertiary dark:bg-dark-text-tertiary flex-shrink-0 mt-1.5" />
              <span><strong className="text-light-text-primary dark:text-dark-text-primary">talents table</strong> — used only by Custom Reports (current state).</span>
            </div>
            <div className="flex items-start gap-2 pt-1 border-t border-primary-500/20">
              <AlertCircle className="w-3.5 h-3.5 text-primary-500 flex-shrink-0 mt-0.5" />
              <span>Deleting or editing a talent does <strong>not</strong> retroactively change event-based statistics.</span>
            </div>
          </div>
        </div>
      </div>

      {/* Each metric as its own subsection */}
      {metrics.map((m, idx) => {
        const Icon = m.icon
        return (
          <div key={m.id}>
            <SubSectionHeading id={m.id}>
              <span className="flex items-center gap-2.5">
                <span className={`inline-flex items-center justify-center w-6 h-6 rounded-md ${m.bg} flex-shrink-0`}>
                  <Icon className={`w-3.5 h-3.5 ${m.color}`} />
                </span>
                {m.title}
                <span className="wiki-mono text-[11px] font-normal text-light-text-tertiary dark:text-dark-text-tertiary">
                  {m.route}
                </span>
              </span>
            </SubSectionHeading>

            <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary leading-relaxed mb-4">
              {m.what}
            </p>

            <div className="rounded-xl border border-light-border-primary dark:border-dark-border-primary overflow-hidden mb-2">
              {/* Events tracked */}
              {m.events ? (
                <div className="px-4 py-3 border-b border-light-border-primary dark:border-dark-border-primary">
                  <p className="wiki-mono text-[10px] uppercase tracking-wider text-light-text-tertiary dark:text-dark-text-tertiary mb-2">
                    Events tracked
                  </p>
                  <div className="space-y-1.5">
                    {m.events.map((e, i) => (
                      <div key={i} className="text-xs flex flex-wrap items-center gap-1.5">{e}</div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="px-4 py-3 border-b border-light-border-primary dark:border-dark-border-primary">
                  <p className="wiki-mono text-[10px] uppercase tracking-wider text-light-text-tertiary dark:text-dark-text-tertiary mb-2">
                    Data source
                  </p>
                  <Tag_ color="purple">talents table — direct query, not events</Tag_>
                </div>
              )}

              {/* Calculation */}
              <div className="px-4 py-3 border-b border-light-border-primary dark:border-dark-border-primary bg-light-bg-secondary dark:bg-dark-bg-secondary">
                <p className="wiki-mono text-[10px] uppercase tracking-wider text-light-text-tertiary dark:text-dark-text-tertiary mb-2">
                  Calculation
                </p>
                {typeof m.calc === 'string'
                  ? <p className="text-xs text-light-text-secondary dark:text-dark-text-secondary leading-relaxed">{m.calc}</p>
                  : m.calc
                }
              </div>

              {/* Filters */}
              <div className="px-4 py-3">
                <p className="wiki-mono text-[10px] uppercase tracking-wider text-light-text-tertiary dark:text-dark-text-tertiary mb-2">
                  Filters
                </p>
                <p className="text-xs text-light-text-secondary dark:text-dark-text-secondary">{m.filters}</p>
              </div>
            </div>

            {/* Note */}
            {m.note && (
              <div className="flex gap-2 items-start">
                <Info className="w-3.5 h-3.5 text-light-text-tertiary dark:text-dark-text-tertiary flex-shrink-0 mt-0.5" />
                <p className="text-xs text-light-text-tertiary dark:text-dark-text-tertiary leading-relaxed">{m.note}</p>
              </div>
            )}
          </div>
        )
      })}

      {/* View modes */}
      <div>
        <SubSectionHeading id="stats-views">View modes</SubSectionHeading>
        <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary mb-4">
          Most metric pages support three ways to view the same data:
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {[
            { icon: Filter,      title: 'List view',    desc: 'Sortable table, one row per manager. Click a row to open the detail modal with a full breakdown.' },
            { icon: LineChart,   title: 'Matrix view',  desc: 'Managers as rows, dates as columns. Each cell = count for that day/week/month. TOTAL row = daily sums; TOTAL column = per-manager sums.' },
            { icon: PieChart,    title: 'Charts view',  desc: 'Line chart (trend), bar chart (manager comparison), pie chart (type breakdown). Supports dark mode.' },
          ].map(v => {
            const Icon = v.icon
            return (
              <div key={v.title} className="flex flex-col gap-3 p-4 rounded-xl border border-light-border-primary dark:border-dark-border-primary bg-light-bg-secondary dark:bg-dark-bg-secondary">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-md bg-primary-50 dark:bg-primary-900/20 flex items-center justify-center flex-shrink-0">
                    <Icon className="w-4 h-4 text-primary-500" />
                  </div>
                  <p className="wiki-heading text-sm font-semibold text-light-text-primary dark:text-dark-text-primary">{v.title}</p>
                </div>
                <p className="text-xs text-light-text-secondary dark:text-dark-text-secondary leading-relaxed">{v.desc}</p>
              </div>
            )
          })}
        </div>
      </div>

      {/* Period comparison */}
      <div>
        <SubSectionHeading id="stats-period">Period comparison</SubSectionHeading>
        <p className="text-sm text-light-text-secondary dark:text-dark-text-secondary mb-4 leading-relaxed">
          Every metric (except Custom Reports) has a period comparison page. Choose two date ranges — the system runs both queries in parallel and shows the difference.
        </p>
        <div className="flex gap-4 p-4 rounded-xl border border-light-border-primary dark:border-dark-border-primary bg-light-bg-secondary dark:bg-dark-bg-secondary">
          <ArrowLeftRight className="w-5 h-5 text-primary-500 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-light-text-secondary dark:text-dark-text-secondary space-y-1">
            <p><strong className="text-light-text-primary dark:text-dark-text-primary">Absolute change</strong> — difference in raw count (e.g. +12 talents)</p>
            <p><strong className="text-light-text-primary dark:text-dark-text-primary">Percentage change</strong> — (period2 − period1) / period1 × 100. Division by zero handled — shows 0% if period1 = 0.</p>
            <p className="text-xs text-light-text-tertiary dark:text-dark-text-tertiary pt-1">
              Available for: Talents Added · Videos · YouTube · Portraits · Hired · Started
            </p>
          </div>
        </div>
      </div>

      {/* FAQ */}
      <div>
        <SubSectionHeading id="stats-faq">Common Questions</SubSectionHeading>
        <div className="divide-y divide-light-border-primary dark:divide-dark-border-primary">
          {faqItems.map(({ key, question, answer }) => (
            <div key={key} id={key} className="scroll-mt-6 group">
              <div className="flex items-center justify-between py-4">
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <button
                    onClick={() => toggle(key)}
                    className="text-sm font-medium text-left text-light-text-primary dark:text-dark-text-primary hover:text-primary-500 transition-colors"
                  >
                    {question}
                  </button>
                  <AnchorLink id={key} />
                </div>
                <button onClick={() => toggle(key)} className="flex-shrink-0 ml-4">
                  <ChevronDown className={['w-4 h-4 text-light-text-tertiary dark:text-dark-text-tertiary transition-transform duration-200', expandedBlocks[key] ? 'rotate-180' : ''].join(' ')} />
                </button>
              </div>
              {expandedBlocks[key] && <div className="pb-4 pr-6">{answer}</div>}
            </div>
          ))}
        </div>
      </div>

    </div>
  )
}

export default StatisticsGuide
